/*
  Card media slider (WonderSkull custom — not part of stock Shrine PRO).

  Extracted from snippets/card-media-slider.liquid so it loads on EVERY page (via one
  <script defer> in layout/theme.liquid), not only where the snippet happens to render at
  initial load. That matters because the product page's "You may also like"
  (<product-recommendations>) and collection facet filtering inject cards by AJAX AFTER
  load — a per-snippet {% javascript %} that isn't in the first render never runs there,
  leaving those sliders dead (arrows do nothing, deferred images never load → blank/black).
  With this loaded globally, the MutationObserver below binds every slider as it appears.
*/
(function () {
  if (window.cardMediaSliderBound) return;
  window.cardMediaSliderBound = true;

  // Theme carousels are carousels at every breakpoint, so skip them outright. The
  // Vibe Collection's own per-breakpoint carousel classes are NOT skipped here — CSS
  // collapses those to one image at the carousel breakpoint, and the guards below keep
  // the (bound but idle) script harmless there.
  var CAROUSEL = '.slider, .splide, splide-component';

  function activeIndex(track) {
    var w = track.clientWidth || 1;
    return Math.round(track.scrollLeft / w);
  }

  function loadDeferred(slide) {
    if (!slide || slide.offsetParent === null) return; // hidden (e.g. collapsed in a carousel) — don't fetch
    var img = slide.querySelector('img[data-cms-src]');
    if (!img) return;
    img.src = img.getAttribute('data-cms-src');
    var ss = img.getAttribute('data-cms-srcset');
    if (ss) img.srcset = ss;
    img.removeAttribute('data-cms-src');
    img.removeAttribute('data-cms-srcset');
  }

  // A Shrine splide rendered as a static grid at both breakpoints (Featured Collection
  // with both "Enable slider" settings off) carries both destroy classes and mounts no
  // carousel engine, so there is no competing swipe — treat it as NOT a carousel.
  var GRID_SPLIDE = '.splide--destroy-desktop.splide--destroy-mobile';

  function initSlider(root) {
    if (root.dataset.cmsReady === 'true') return;
    var carousel = root.closest(CAROUSEL);
    if (carousel && !carousel.matches(GRID_SPLIDE)) return;
    root.dataset.cmsReady = 'true';

    var track = root.querySelector('.card-media-slider__track');
    var slides = root.querySelectorAll('.card-media-slider__slide');
    var bar = root.querySelector('.card-media-slider__bar-fill');
    var prev = root.querySelector('[data-cms-prev]');
    var next = root.querySelector('[data-cms-next]');
    if (!track || slides.length < 2) return;

    // The moment the shopper first engages this card (swipe, scroll or an arrow),
    // fetch every remaining image at once, so the rest of the gallery is ready and
    // later swipes never wait on a load. Nothing loads until that first engagement.
    var loadedAll = false;
    function loadAll() {
      if (loadedAll) return;
      loadedAll = true;
      for (var s = 0; s < slides.length; s++) loadDeferred(slides[s]);
    }

    // Start fetching the deferred images at the earliest hint of intent — a finger
    // landing (touchstart, below) or the pointer entering on desktop — so the image is
    // already loading before the first swipe lands or an arrow is clicked, instead of
    // flashing blank. Still nothing loads until the shopper actually engages the card.
    root.addEventListener('mouseenter', loadAll);

    var last = slides.length - 1;

    function update() {
      if (bar) bar.style.setProperty('--cms-index', activeIndex(track));
    }

    // Loops both ways: past the last wraps to the first and vice versa. The wrap jump
    // is instant (no long rewind); ordinary steps animate.
    function go(dir) {
      loadAll();
      var i = activeIndex(track) + dir;
      var wrap = false;
      if (i > last) { i = 0; wrap = true; }
      else if (i < 0) { i = last; wrap = true; }
      track.scrollTo({ left: i * track.clientWidth, behavior: wrap ? 'auto' : 'smooth' });
    }

    if (prev) {
      prev.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        go(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        go(1);
      });
    }

    var scrollTimer;
    track.addEventListener('scroll', function () {
      loadAll();
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(update, 60);
    });

    // Mobile loop: sitting on the last image and swiping left again jumps to the first.
    // Touch (not pointer) events are used because a scroll gesture fires pointercancel,
    // not pointerup — touchend always fires at the gesture's end.
    var touchX = 0;
    var touchStartIndex = 0;
    track.addEventListener('touchstart', function (e) {
      loadAll();
      root.classList.add('is-swiping'); // grow the position bar while the finger is down
      touchX = e.touches[0].clientX;
      touchStartIndex = activeIndex(track);
    }, { passive: true });
    // Shrink the bar back the moment the finger lifts (or the touch is cancelled).
    track.addEventListener('touchcancel', function () {
      root.classList.remove('is-swiping');
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      root.classList.remove('is-swiping');
      var end = e.changedTouches[0] ? e.changedTouches[0].clientX : touchX;
      var dx = end - touchX;
      var maxScroll = track.scrollWidth - track.clientWidth;
      // Started on the last image, a firm leftward swipe, and the scroll stayed pinned
      // at the end (nothing more to scroll) — wrap to the first.
      if (touchStartIndex === last && dx < -40 && track.scrollLeft >= maxScroll - 4) {
        loadAll();
        // Rewind smoothly (~0.5s) rather than snapping, so it reads as "back to the
        // first image". Snap is turned off for the animation because scroll-snap-stop
        // would otherwise halt a programmatic scroll at the next slide; it is restored
        // once the rewind lands. Scroll events during the animation keep the bar in step.
        track.style.scrollSnapType = 'none';
        track.scrollTo({ left: 0, behavior: 'smooth' });
        window.setTimeout(function () { track.style.scrollSnapType = ''; }, 650);
      }
    }, { passive: true });

    // Tap vs swipe. When the card body is a real <a> (Vibe Collection), that link
    // already navigates on tap, so nothing to do here. In the theme card (stretched
    // link, track sits above it) a genuine tap is forwarded to the product URL.
    if (!root.closest('a')) {
      var startX = 0;
      var startY = 0;
      var moved = false;
      track.addEventListener('pointerdown', function (e) {
        startX = e.clientX;
        startY = e.clientY;
        moved = false;
      });
      track.addEventListener('pointermove', function (e) {
        if (Math.abs(e.clientX - startX) > 8 || Math.abs(e.clientY - startY) > 8) moved = true;
      });
      track.addEventListener('click', function (e) {
        if (moved) return;
        if (e.target.closest('[data-cms-prev], [data-cms-next]')) return;
        var url = root.getAttribute('data-cms-url');
        if (url) window.location.href = url;
      });
    }

    update();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-card-media-slider]').forEach(initSlider);
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  // Cards injected after load never see initAll() above, so nothing binds to them:
  // Shopify's <product-recommendations> ("You may also like") fetches the related
  // products by AJAX after the page loads, and collection facet filtering re-renders
  // the product grid the same way. Without this, those sliders render (the bar and
  // arrows are pure CSS) but stay dead — the deferred images never load (blank slides),
  // the bar never moves, and the arrows do nothing. Watch for inserted sliders and
  // init them as they appear. initSlider is idempotent (guards on data-cms-ready).
  var observer = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var n = 0; n < added.length; n++) {
        var node = added[n];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-card-media-slider]')) initSlider(node);
        if (node.querySelectorAll) node.querySelectorAll('[data-card-media-slider]').forEach(initSlider);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
